import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, Box, Typography, Stack, Chip } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import GeekPlayer from '../../../../components/GeekPlayer';

interface EmployerCardProps {
  employer: {
    uuid: string;
    slug: string;
    companyName: string;
    companyAddress: string;
    introVideoUuid: string;
    about: string;
    activePositionsCount?: number;
  };
  isActive: boolean;
  playing: boolean;
  setPlaying: (playing: boolean) => void;
  muted: boolean;
  setMuted: (muted: boolean) => void;
  onVisibilityChange: (isVisible: boolean) => void;
}

const EmployerCard: React.FC<EmployerCardProps> = ({ 
  employer,
  isActive,
  playing,
  setPlaying,
  muted,
  setMuted,
  onVisibilityChange
}) => {
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  // Detect mobile on mount and window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // IntersectionObserver for mobile autoplay
  useEffect(() => {
    if (!isMobile) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisibilityChange(true);
          } else {
            onVisibilityChange(false);
          }
        });
      },
      {
        threshold: 0.7, // Card is considered visible when 70% is in view
        rootMargin: '0px 0px -20% 0px' // Consider card visible when it's in the top 80% of the viewport
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [isMobile, onVisibilityChange]);

  const handleCardClick = () => {
    navigate(`/apply/${employer.slug}`);
  };

  // Determine if video should play based on device type
  const shouldPlayVideo = isMobile ? (isActive && playing) : (isHovered && playing);

  return (
    <Card
      ref={cardRef}
      variant="outlined"
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        },
      }}
      onClick={handleCardClick}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
    >
      {/* Video Section */}
      <Box
        sx={{
          width: '100%',
          aspectRatio: '3/4',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {employer.introVideoUuid ? (
          <GeekPlayer
            key={employer.introVideoUuid}
            videoId={employer.introVideoUuid}
            playing={shouldPlayVideo}
            setGlobalPlaying={setPlaying}
            globalMuted={muted}
            setGlobalMuted={setMuted}
            showMuteButton={false}
            showScrubber={false}
            aspectRatio="3/4"
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: '#ECEFF3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography level="body-md" sx={{ color: '#7B7F85' }}>
              No video available
            </Typography>
          </Box>
        )}
      </Box>

      {/* Content Section */}
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
            {employer.companyName}
          </Typography>
          
          {employer.companyAddress && (
            <Typography level="body-sm" sx={{ color: '#7B7F85' }}>
              📍 {employer.companyAddress}
            </Typography>
          )}
          
          {employer.activePositionsCount !== undefined && employer.activePositionsCount > 0 && (
            <Chip
              size="sm"
              variant="soft"
              color="primary"
              sx={{ width: 'fit-content' }}
            >
              {employer.activePositionsCount} {employer.activePositionsCount === 1 ? 'position' : 'positions'} available
            </Chip>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default EmployerCard;

